import {
  Card,
  ExtraTranslationLanguage,
  TranslationLanguage,
  Translations,
  mergeTranslations,
  missingTranslationLanguages,
  sanitizeTranslations,
} from '@lingoping/core';
import { SupabaseClient, FunctionsHttpError } from '@supabase/supabase-js';
import { getSupabase, isSupabaseConfigured } from '../config/supabase.js';
import { StorageManager, isDevMockUserId } from '../storage/index.js';
import { sameTranslations } from './cardRows.js';
import { AccessLockedError, isAccessLocked, throwIfAccessLocked } from './access.js';
import { RETRY_BACKOFF_MS } from './cardImageService.js';
import { getBaseTranslationLanguage } from './translationSettings.js';

/**
 * Backfill: gives every local card the translations it is missing (all extra languages, meaning
 * and example). Every word, deck words included, goes to the `translate-words` Edge Function in
 * batches of 20, the language being shown first. Each change is saved as a change of the card's
 * text (`StorageManager.updateCardContent`), so it reaches the account like any other.
 *
 * Runs only for a real signed-in account (never the dev-mock sign-in), when the dashboard or a
 * review window opens and from the service worker's 15-minute alarm. A card that is still
 * incomplete after a run waits (5 minutes up to 6 hours, like the picture retry) before it is
 * tried again, and one run handles a limited number of cards.
 */

const BATCH_SIZE = 20;
/** Cards sent to the server per run (3 full batches); the rest wait for the next run. */
export const MAX_CARDS_PER_RUN = 60;
const RETRY_STATE_KEY = 'lingoping_translation_retry_v1';

export interface TranslationRetryEntry {
  attempts: number;
  nextAttemptAt: number;
}

interface RetryState {
  cards: Record<string, TranslationRetryEntry>;
  /** UTC day (YYYY-MM-DD) on which the server said the daily limit is used up. */
  limitDay?: string;
}

export interface BackfillOptions {
  /** Cards to do first (the review window's session cards). */
  priorityCardIds?: string[];
  /** The language being shown, translated before the others. Defaults to the base language. */
  language?: TranslationLanguage;
  maxCards?: number;
  /** Called with each card after new translations were saved to it. */
  onCardUpdated?: (card: Card) => void;
  now?: number;
}

export interface BackfillResult {
  /** Cards that received at least one translation from the server. */
  translated: number;
  /** Cards the server could not (fully) translate; they are retried after a delay. */
  failed: number;
}

const today = (now: number) => new Date(now).toISOString().slice(0, 10);
const cardLemma = (card: Card) => card.lemma ?? card.front_text;

function backoffFor(attempts: number): number {
  return RETRY_BACKOFF_MS[Math.min(attempts - 1, RETRY_BACKOFF_MS.length - 1)];
}

async function readState(): Promise<RetryState> {
  const stored = await StorageManager.getLocalValue<RetryState>(RETRY_STATE_KEY);
  return { cards: stored?.cards ?? {}, limitDay: stored?.limitDay };
}

/** The client for a real signed-in session, or null (signed out, dev-mock sign-in, no server). */
async function realSupabase(): Promise<SupabaseClient | null> {
  const userId = await StorageManager.getCurrentUserId();
  if (!userId || isDevMockUserId(userId)) return null;
  const profile = await StorageManager.getProfile();
  const key = profile.supabase_publishable_key || profile.supabase_anon_key;
  if (!isSupabaseConfigured(profile.supabase_url, key)) return null;
  const supabase = getSupabase(profile.supabase_url, key);
  const { data } = await supabase.auth.getSession();
  return data.session ? supabase : null;
}

/**
 * Which cards to send to the server now: something still missing, not waiting out a backoff. Priority cards first, then those missing the shown language.
 */
export function selectCardsForTranslation(
  cards: Card[],
  state: Record<string, TranslationRetryEntry>,
  options: {
    priorityCardIds?: string[];
    language?: TranslationLanguage;
    now?: number;
    limit?: number;
  } = {}
): Card[] {
  const { priorityCardIds = [], language, now = Date.now(), limit = MAX_CARDS_PER_RUN } = options;
  const priority = new Set(priorityCardIds);
  const eligible = cards.filter(
    (c) =>
      missingTranslationLanguages(c).length > 0 &&
      (!state[c.id] || state[c.id].nextAttemptAt <= now)
  );
  const rank = (c: Card) =>
    priority.has(c.id)
      ? 0
      : language && language !== 'en' && missingTranslationLanguages(c).includes(language)
        ? 1
        : 2;
  return eligible
    .map((c, i) => ({ c, i }))
    .sort((a, b) => rank(a.c) - rank(b.c) || a.i - b.i)
    .slice(0, limit)
    .map((x) => x.c);
}

/** Stores new translations on the card as it is now in storage (a review meanwhile is kept). */
async function saveTranslations(
  cardId: string,
  add: Translations
): Promise<{ card: Card; changed: boolean } | null> {
  const current = (await StorageManager.getCards()).find((c) => c.id === cardId);
  if (!current) return null;
  const translations = mergeTranslations(current.translations, add);
  if (sameTranslations(translations, current.translations)) {
    return { card: current, changed: false };
  }
  // Only the new translations travel; the server adds them to the ones the card has.
  const updated = await StorageManager.updateCardContent(cardId, { translations: add });
  return updated ? { card: updated, changed: true } : null;
}

async function errorCode(error: unknown): Promise<string | undefined> {
  if (!(error instanceof FunctionsHttpError)) return undefined;
  try {
    return (await (error.context as Response).clone().json())?.error;
  } catch {
    return undefined;
  }
}

interface BatchOutcome {
  /** One entry per card of the batch, in order: the translations, or null when it failed. */
  results: Array<Translations | null>;
  /** The whole call failed, the daily limit was hit or access is locked: stop the run. */
  stop?: 'limit' | 'error' | 'locked';
}

async function translateBatch(
  supabase: SupabaseClient,
  batch: Card[],
  targets: ExtraTranslationLanguage[]
): Promise<BatchOutcome> {
  const failedAll = (stop: 'limit' | 'error' | 'locked'): BatchOutcome => ({
    results: batch.map(() => null),
    stop,
  });

  let data: { results?: Array<Record<string, unknown>> } | null;
  try {
    const res = await supabase.functions.invoke<{ results?: Array<Record<string, unknown>> }>(
      'translate-words',
      {
        body: {
          language: 'de',
          words: batch.map((c) => ({
            lemma: cardLemma(c),
            translation: c.back_text,
            example_de: c.example_target,
            example_en: c.example_native,
          })),
          targets,
        },
      }
    );
    if (res.error) {
      await throwIfAccessLocked(res.error);
      const code = await errorCode(res.error);
      console.warn('[LingoPing] translate-words failed:', code ?? res.error.message);
      return failedAll(code === 'daily_limit_reached' ? 'limit' : 'error');
    }
    data = res.data;
  } catch (err) {
    if (err instanceof AccessLockedError) return failedAll('locked');
    console.warn('[LingoPing] translate-words failed:', (err as Error).message);
    return failedAll('error');
  }

  const returned = data?.results ?? [];
  let stop: BatchOutcome['stop'];
  const results = batch.map((card, i) => {
    const lemma = cardLemma(card).toLowerCase();
    // Results line up with the request; fall back to matching by word if the order differs.
    const r =
      String(returned[i]?.lemma ?? '').toLowerCase() === lemma
        ? returned[i]
        : returned.find((x) => String(x.lemma ?? '').toLowerCase() === lemma);
    if (!r || r.error) {
      if (r?.error === 'daily_limit_reached') stop = 'limit';
      return null;
    }
    const translations = sanitizeTranslations(r.translations);
    return Object.keys(translations).length > 0 ? translations : null;
  });
  return { results, stop };
}

async function runBackfill(options: BackfillOptions): Promise<BackfillResult> {
  const result: BackfillResult = { translated: 0, failed: 0 };
  const supabase = await realSupabase();
  if (!supabase) return result;
  // No access: nothing to ask the server, and nothing to count as a failed try.
  if (await isAccessLocked()) return result;

  const now = options.now ?? Date.now();
  const language = options.language ?? (await getBaseTranslationLanguage());

  // A chosen word whose card text is still being prepared has nothing to translate yet.
  const showable = (cards: Card[]) => cards.filter((c) => c.content_status !== 'pending');
  const state = await readState();
  if (state.limitDay === today(now)) return result;

  const cards = showable(await StorageManager.getCards());
  const selected = selectCardsForTranslation(cards, state.cards, {
    priorityCardIds: options.priorityCardIds,
    language,
    now,
    limit: options.maxCards,
  });
  if (selected.length === 0) return result;

  const live = new Map(selected.map((c) => [c.id, c]));
  const failed = new Set<string>();
  const improved = new Set<string>();
  let stopped = false;
  let locked = false;

  const pass = async (group: Card[], targetsFor: (batch: Card[]) => ExtraTranslationLanguage[]) => {
    for (let start = 0; start < group.length && !stopped; start += BATCH_SIZE) {
      const batch = group.slice(start, start + BATCH_SIZE);
      const outcome = await translateBatch(supabase, batch, targetsFor(batch));
      for (const [i, card] of batch.entries()) {
        const translations = outcome.results[i];
        if (!translations) {
          failed.add(card.id);
          continue;
        }
        const saved = await saveTranslations(card.id, translations);
        if (!saved) continue;
        live.set(card.id, saved.card);
        if (!saved.changed) continue;
        improved.add(card.id);
        options.onCardUpdated?.(saved.card);
      }
      if (outcome.stop) {
        stopped = true;
        if (outcome.stop === 'locked') locked = true;
        if (outcome.stop === 'limit') state.limitDay = today(now);
      }
    }
  };

  // The language being shown first, so a card is useful as soon as possible; then everything else.
  if (language !== 'en') {
    const lang = language as ExtraTranslationLanguage;
    await pass(
      selected.filter((c) => missingTranslationLanguages(live.get(c.id) ?? c).includes(lang)),
      () => [lang]
    );
  }
  await pass(
    selected.filter(
      (c) => !failed.has(c.id) && missingTranslationLanguages(live.get(c.id) ?? c).length > 0
    ),
    (batch) => [...new Set(batch.flatMap((c) => missingTranslationLanguages(live.get(c.id) ?? c)))]
  );

  // Locked in the middle of the run: the cards that failed did not fail by themselves.
  if (locked) return result;

  // Cards that are complete now are forgotten; cards still incomplete wait before the next try.
  for (const card of selected) {
    const current = live.get(card.id) ?? card;
    if (missingTranslationLanguages(current).length === 0) {
      delete state.cards[card.id];
    } else if (failed.has(card.id) || improved.has(card.id) || !stopped) {
      const attempts = (state.cards[card.id]?.attempts ?? 0) + 1;
      state.cards[card.id] = { attempts, nextAttemptAt: now + backoffFor(attempts) };
      result.failed++;
    }
  }
  result.translated = improved.size;

  // Forget cards that were deleted.
  const liveIds = new Set((await StorageManager.getCards()).map((c) => c.id));
  for (const id of Object.keys(state.cards)) if (!liveIds.has(id)) delete state.cards[id];
  await StorageManager.setLocalValue(RETRY_STATE_KEY, state);
  return result;
}

let queue: Promise<unknown> = Promise.resolve();

/**
 * Runs the translation backfill (see the top of this file). Runs are queued one after another, so
 * the dashboard, a review window and the alarm in the same page never overlap. Never throws.
 */
export function runTranslationBackfill(options: BackfillOptions = {}): Promise<BackfillResult> {
  const run = queue.then(() => runBackfill(options));
  const settled = run.catch((err) => {
    console.warn('[LingoPing] Translation backfill failed:', (err as Error).message);
    return { translated: 0, failed: 0 } as BackfillResult;
  });
  queue = settled;
  return settled;
}
